import { useCallback, useMemo } from 'react';

import { Capacitor } from '@capacitor/core';
import { useQueryClient } from '@tanstack/react-query';

import { useGetApiV5Me } from '@liscio/api';
import { CpaStateItem, useCpaStore, useInitializedFlags } from '@liscio/common';

import {
  buildPostCpaSwitchRedirectUrl,
  saveApplicationCpa,
  makeStateCpaFromV5Cpa,
  normalizeCompanyHost,
} from './cpa-utils';
import { useSwitchCpaMutationV5 } from 'fetch-utils/users/auth-hooks';

/**
 * Convenience hook for managing user Cpa(s)
 * NOTE: This hook should only be used in authorized views since it
 * utilizes home data which requires an auth token
 * TODO: Handle parsing cpa id from query parameters to select cpa
 */
export const useCpaV5 = () => {
  const {
    isInitialized: isFFInitialized,
    flags: { v5Login },
  } = useInitializedFlags();

  const { isLoading, isInitialized, sessionCpa, appCpa, setAppCpa } =
    useCpaStore();
  const {
    mutateAsync: switchCpaMutation,
    isLoading: isSwitchingCpa,
    error: switchCpaError,
  } = useSwitchCpaMutationV5();
  const queryClient = useQueryClient();

  const { data: meData, isLoading: isLoadingMeData } = useGetApiV5Me({
    query: { enabled: isFFInitialized && v5Login },
  });

  // List of user related cpas
  const cpas = useMemo(() => {
    const meDataCpa = meData?.cpa_data || [];
    return meDataCpa.map(makeStateCpaFromV5Cpa);
  }, [meData?.cpa_data]);

  // Select Application Cpa
  const selectCpa = useCallback(
    async (cpa: CpaStateItem) => {
      // Only switch cpa in backend if different from existing session cpa.
      // Normalized compare: sessionCpa.custom_liscio_url comes from home data
      // while cpa urls come from /me, so a formatting difference (scheme,
      // trailing slash, case) must not be mistaken for a different firm — a
      // false mismatch here costs a needless token swap + query-cache clear
      // (LISD-2544).
      if (
        !sessionCpa ||
        normalizeCompanyHost(sessionCpa.custom_liscio_url) !==
          normalizeCompanyHost(cpa.custom_liscio_url)
      ) {
        await switchCpaMutation({ cpa_uuid: cpa.cpa_uuid! });
      }
      setAppCpa(cpa);
      await saveApplicationCpa(cpa);
    },
    [sessionCpa, setAppCpa, switchCpaMutation]
  );

  const switchCpa = useCallback(
    async (cpaUrl: string) => {
      const selectedCpa = cpas.find((cpa) => cpa.custom_liscio_url === cpaUrl);

      if (selectedCpa) {
        try {
          await switchCpaMutation({ cpa_uuid: selectedCpa.cpa_uuid! });

          const newCpa = { ...selectedCpa };
          setAppCpa(newCpa);
          await saveApplicationCpa(newCpa);

          if (Capacitor.isNativePlatform()) {
            // On native platforms we refresh the user session
            queryClient.invalidateQueries();
          } else {
            // On web, we redirect
            window.location.href = buildPostCpaSwitchRedirectUrl(newCpa);
          }
        } catch {}
      }
    },
    [switchCpaMutation, setAppCpa, cpas, queryClient]
  );

  return {
    cpa: appCpa,
    cpas,
    selectCpa,
    isLoading: !isInitialized || isLoading || isSwitchingCpa || isLoadingMeData,
    switchCpa,
    error: switchCpaError,
  };
};
